// @ts-nocheck
import Backbone, { Model } from 'backbone';
import Service from 'views/store/common/utility/model/service';
import { Collection } from 'views/store/common/utility/adt/collection';
import Utils from 'views/store/common/utility';

import { ProjectChecklistModel } from 'models/store/client/projects/roomchecklists/project_checklist';

export class ProjectChecklistsModel extends Model {
	static propertiesCustom = ['step', 'checklists', 'pagination', 'paginated'];

	static steps = {
		step1: 'step1',
		step2: 'step2'
	};

	defaults() {
		return {
			step: ProjectChecklistsModel.steps.step1,
			projectId: null,
			projectName: null,
			shouldRedirect: true,
			createDefaultGroup: false,
			pagination: { current: 1, perPage: 8, totalPages: 0 },
			checklists: new Collection([], { _interface: ProjectChecklistModel }),
			paginated: new Collection([], { _interface: ProjectChecklistModel })
		};
	}

	url() {
		return '/app/api';
	}

	initialize(options) {
		super.initialize(options);
		Service.proxify(this).attachEvents();
		this.getData(options);
	}

	attachEvents() {
		this.get('paginated').on('change:selected', this.updateSelection, this);
	}

	matcher(checklist, current) {
		return checklist.get('id') === current.get('id');
	}

	getProperties(...properties) {
		return Utils.pick(this.toJSON(), properties);
	}

	toJSON(options = {}) {
		return {
			...Utils.omit(super.toJSON(options), ProjectChecklistsModel.propertiesCustom),
			pagination: this.get('pagination'),
			checklists: this.get('checklists').toJSON()
		};
	}

	createProject(success, error) {
		return this.request(
			{
				path: '/projects/v2/create',
				data: JSON.stringify({
					name: this.get('projectName'),
					checklistIds: this.getSelectedChecklistIds(),
					createDefaultGroup: this.get('createDefaultGroup')
				}),
				contentType: 'application/json',
				onParse: this.onProjectCreate.bind(this)
			},
			success,
			error
		);
	}

	createGroupsForProject(success, error) {
		return this.request(
			{
				path: `/projects/v2/${this.get('projectId')}/checklistGroups/create`,
				type: 'POST',
				data: JSON.stringify({ checklistGroupData: this.getSelectedChecklistData() }),
				contentType: 'application/json'
			},
			success,
			error
		);
	}

	getData(options, success, error) {
		return this.request(
			{
				path: '/shoppinglists/v2/checklists',
				type: 'GET',
				contentType: 'application/json',
				onParse: (response) => this.onFetch(response, options)
			},
			success,
			error
		);
	}

	onProjectCreate(response = {}) {
		if (Utils.defined(response.id)) {
			this.sendCreateProjectTracking();
			this.get('shouldRedirect')
				? window.location.assign(`/account/projects/${response.id}/feed`)
				: Backbone.trigger('project:saved', response);
		}
		return {};
	}

	sendCreateProjectTracking() {
		const checklistRoomsArray = this.getSelectedChecklistData();
		const roomsString = checklistRoomsArray.length ? checklistRoomsArray.map((room) => room.name).join(',') : '';
		Backbone.trigger('omniture:projects', 'project_creation_modal', 'Create Project', { roomType: roomsString });
	}

	getSelectedChecklistIds() {
		return this.get('checklists').flatMap((checklist) => (checklist.get('selected') ? checklist.get('id') : null));
	}

	getSelectedChecklistData() {
		return this.get('checklists').flatMap((checklist) =>
			checklist.get('selected') ? { id: checklist.get('id'), name: checklist.get('name') } : null
		);
	}

	hasPrev() {
		return this.get('pagination').current > 1;
	}

	hasNext() {
		return this.get('pagination').current < this.get('pagination').totalPages;
	}

	isAnythingSelected() {
		return this.get('checklists').some((checklist) => checklist.get('selected'));
	}

	clearSelection() {
		this.get('checklists').update(() => true, 'set', [{ selected: false }, { silent: true }]);
		return this.update();
	}

	prev() {
		if (this.hasPrev()) {
			const pagination = this.get('pagination');
			this.set('pagination', { ...pagination, current: pagination.current - 1 });
			this.update();
		}
		return this;
	}

	next() {
		if (this.hasNext()) {
			const pagination = this.get('pagination');
			this.set('pagination', { ...pagination, current: pagination.current + 1 });
			this.update();
		}
		return this;
	}

	select(memo, checklist, ix) {
		const pagination = this.get('pagination'),
			max = pagination.current * pagination.perPage,
			min = max - pagination.perPage;
		if (ix >= min && ix < max) {
			memo.push(checklist.toJSON());
		}
		return memo;
	}

	changeStep(step = ProjectChecklistsModel.steps.step1) {
		this.set('step', step);
		return this;
	}

	update(options = {}) {
		this.get('paginated').set(this.get('checklists').reduce(this.select, [], this), { ...options, new: ProjectChecklistModel.new });
		return this;
	}

	updateSelection(checklist) {
		this.get('checklists').update(this.matcher.bind(this, checklist), 'set', [
			{ selected: checklist.get('selected') },
			{ silent: true }
		]);
		return this;
	}

	onFetch(response, options) {
		this.onFetchChecklists(response.checklists, { silent: true });
		this.onFetchPagination(response.pagination, { silent: true });
		this.set(Utils.omit(response, ProjectChecklistsModel.propertiesCustom), options).update();
		this.trigger('dataLoaded');
		return {};
	}

	onFetchPagination(response = {}, options = {}) {
		this.set('pagination', response, options);
	}

	onFetchChecklists(response = [], options = {}) {
		if (Array.isArray(response) && response.length > 0) {
			this.get('checklists').set(response, { ...options, new: ProjectChecklistModel.new });
		}
	}
}
